import {
    normalizeWidgetTheme,
    APIURL,
    appendFlatpickrBaseStyles,
    initFlatpickrInShadow,
    cancelSelectEvent,
    changeSymbolForWidget,
    getCSSStyles,
    getSymbolDropdown,
    getTraderSMCardHTML,
    getWidgetDataURI,
    apiGet,
    toggleWidgetTheme,
    getEChartsTooltipTheme,
    getHeaderPinStateClass,
    getHeaderPinButtonHTML,
    setupHeaderPin,
    getNoDataHTML
} from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import { format } from 'date-fns';

type NetIncomeMetric = 'employee_count' | 'net_income';

interface NetIncomeDataPoint {
    date: string;
    year: number;
    employee_count?: number;
    net_income?: number;
    net_income_millions?: number;
    display_value?: string;
}

interface NetIncomePerEmployeeConfig extends WidgetConfig {
    start_date?: string;
    end_date?: string;
    metric?: NetIncomeMetric;
    dateRange?: string;
    availableDateRanges?: string[];
    enableCalendar?: boolean;
    onDateChange?: (startDate: string, endDate: string) => void;
    onDateRangeChange?: (range: string) => void;
}

const METRIC_OPTIONS: { value: NetIncomeMetric; label: string; color: string }[] = [
    { value: 'employee_count', label: 'Employee Count', color: '#7C4DFF' },
    { value: 'net_income', label: 'Net Income', color: '#33D17A' }
];

export class TraderverseNetIncomePerEmployee {
    private container: HTMLElement;
    private config: NetIncomePerEmployeeConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: NetIncomeDataPoint[] | null = null;
    private currentChart: any;
    private selectedMetrics: Set<NetIncomeMetric> = new Set(['employee_count', 'net_income']);
    private startDate: string = '';
    private endDate: string = '';
    private selectedRange: string = 'Last 6 Years';
    private datePickerInstance: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;

    constructor(config: NetIncomePerEmployeeConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        if (config.dateRange) this.selectedRange = config.dateRange;
        if (config.metric) this.selectedMetrics = new Set([config.metric]);
        this.applyConfigDates();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<NetIncomePerEmployeeConfig> {
        const symbols = this.fetchSymbols();
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            start_date: '2020-01-01',
            end_date: '2026-12-31',
            dateRange: 'Last 6 Years',
            availableDateRanges: [
                'Last 1 Year',
                'Last 2 Years',
                'Last 3 Years',
                'Last 5 Years',
                'Last 6 Years'
            ],
            enableCalendar: true
        };
    }

    private applyConfigDates(): void {
        if (this.config.start_date && this.config.end_date) {
            this.startDate = this.config.start_date;
            this.endDate = this.config.end_date;
            return;
        }
        this.setDateRange(this.selectedRange);
    }

    private setDateRange(range: string): void {
        const today = new Date();
        let start = new Date();

        switch (range || 'Last 6 Years') {
            case 'Last 1 Year':
                start.setFullYear(today.getFullYear() - 1);
                break;
            case 'Last 2 Years':
                start.setFullYear(today.getFullYear() - 2);
                break;
            case 'Last 3 Years':
                start.setFullYear(today.getFullYear() - 3);
                break;
            case 'Last 5 Years':
                start.setFullYear(today.getFullYear() - 5);
                break;
            case 'Last 6 Years':
            default:
                start = new Date('2020-01-01');
                break;
        }

        if (range === 'Last 6 Years') {
            this.startDate = this.config.start_date || '2020-01-01';
            this.endDate = this.config.end_date || '2026-12-31';
            return;
        }

        this.startDate = start.toISOString().split('T')[0];
        this.endDate = today.toISOString().split('T')[0];
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private renderLegendHtml(): string {
        return METRIC_OPTIONS.map(opt => {
            const isActive = this.selectedMetrics.has(opt.value) ? ' active' : '';
            return `<span class="toggle-series${isActive}" data-metric="${opt.value}" data-tooltip="${opt.label}" style="background-color:${opt.color};" data-testing-id="legend-${opt.value}"></span>`;
        }).join('');
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        this.setupEventListeners();
        await this.fetchData(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
        setTimeout(() => this.initDatePicker(), 0);
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Net Income Per Employee</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow" data-testing-id="options-row">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                    <div class="trader-sm-card" id="selected-symbol">
                        ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                    </div>
                    <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                <span></span>
                <div class="item dateRange" data-testing-id="date-range">
                    <input type="text" class="date-input" data-testing-id="date-input" />
                    <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
                </div>
                <div class="item" data-testing-id="legends-container">
                    <div class="legendsCollection" data-testing-id="legends-collection">
                        ${this.renderLegendHtml()}
                    </div>
                </div>
                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <label>Date Range</label>
                    <select class="range-select">
                      ${(this.config.availableDateRanges || [])
                            .map(range => `<option value="${range}"${range === this.selectedRange ? ' selected' : ''}>${range}</option>`)
                            .join('')}
                    </select>
                  </div>
                  <div class="item">
                    <label>Metric</label>
                    <select class="metric-select">
                      <option value="both"${this.selectedMetrics.size === 2 ? ' selected' : ''}>Both</option>
                      ${METRIC_OPTIONS.map(opt =>
                            `<option value="${opt.value}"${this.selectedMetrics.size === 1 && this.selectedMetrics.has(opt.value) ? ' selected' : ''}>${opt.label}</option>`
                        ).join('')}
                    </select>
                  </div>
                  ${this.config.enableCalendar !== false ? `
                  <div class="item date-picker">
                    <label>Custom Range</label>
                    <input type="text" class="filter-date-input" />
                  </div>` : ''}
                </div>
            </div>
            <div class="chartDv" data-testing-id="chart-container">
                <div class="chart" id="netIncomePerEmployeeChart" data-testing-id="net-income-per-employee-chart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) placeholder.replaceWith(dropdown);
        }

        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        const rangeSelect = this.shadowRoot.querySelector('.range-select') as HTMLSelectElement;
        rangeSelect?.addEventListener('change', () => {
            this.selectedRange = rangeSelect.value;
            this.setDateRange(this.selectedRange);
            this.config.start_date = this.startDate;
            this.config.end_date = this.endDate;
            this.syncDateInputs();
            this.fetchData(this.config.symbol!);
            this.config.onDateRangeChange?.(this.selectedRange);
        });

        const metricSelect = this.shadowRoot.querySelector('.metric-select') as HTMLSelectElement;
        metricSelect?.addEventListener('change', () => {
            const value = metricSelect.value;
            if (value === 'both') {
                this.selectedMetrics = new Set(['employee_count', 'net_income']);
            } else {
                this.selectedMetrics = new Set([value as NetIncomeMetric]);
            }
            this.config.metric = this.selectedMetrics.size === 1
                ? Array.from(this.selectedMetrics)[0]
                : undefined;
            this.refreshLegends();
            this.updateDisplay();
        });
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private initDatePicker(): void {
        const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
        if (dateInput) {
            dateInput.value = `${this.startDate} to ${this.endDate}`;
            initFlatpickrInShadow(dateInput, this.shadowRoot, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                defaultDate: [this.startDate, this.endDate],
                onClose: (selectedDates: Date[]) => {
                    if (selectedDates.length === 2) {
                        const [start, end] = selectedDates;
                        this.startDate = start.toISOString().slice(0, 10);
                        this.endDate = end.toISOString().slice(0, 10);
                        this.config.start_date = this.startDate;
                        this.config.end_date = this.endDate;
                        dateInput.value = `${this.startDate} to ${this.endDate}`;
                        this.syncFilterDateInput();
                        this.fetchData(this.config.symbol!);
                        this.config.onDateChange?.(this.startDate, this.endDate);
                    }
                }
            });
        }

        const filterDateInput = this.shadowRoot.querySelector('.filter-date-input') as HTMLInputElement | null;
        if (filterDateInput) {
            filterDateInput.value = `${this.startDate} to ${this.endDate}`;
            this.datePickerInstance = initFlatpickrInShadow(filterDateInput, this.shadowRoot, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                defaultDate: [this.startDate, this.endDate],
                onClose: (selectedDates: Date[]) => {
                    if (selectedDates.length === 2) {
                        const [start, end] = selectedDates;
                        this.startDate = format(start, 'yyyy-MM-dd');
                        this.endDate = format(end, 'yyyy-MM-dd');
                        this.config.start_date = this.startDate;
                        this.config.end_date = this.endDate;
                        this.syncDateInputs();
                        this.fetchData(this.config.symbol!);
                        this.config.onDateChange?.(this.startDate, this.endDate);
                    }
                }
            });
        }
    }

    private syncDateInputs(): void {
        const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
        if (dateInput) dateInput.value = `${this.startDate} to ${this.endDate}`;
        this.syncFilterDateInput();
    }

    private syncFilterDateInput(): void {
        const filterDateInput = this.shadowRoot.querySelector('.filter-date-input') as HTMLInputElement | null;
        if (filterDateInput) filterDateInput.value = `${this.startDate} to ${this.endDate}`;
    }

    private refreshLegends(): void {
        const legendsCollection = this.shadowRoot.querySelector('.legendsCollection') as HTMLElement | null;
        if (!legendsCollection) return;
        legendsCollection.innerHTML = this.renderLegendHtml();
        this.bindLegendToggles();
        const metricSelect = this.shadowRoot.querySelector('.metric-select') as HTMLSelectElement | null;
        if (metricSelect) {
            metricSelect.value = this.selectedMetrics.size === 2
                ? 'both'
                : Array.from(this.selectedMetrics)[0];
        }
    }

    private bindLegendToggles(): void {
        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        legendSpans.forEach(span => {
            span.setAttribute('role', 'button');
            span.tabIndex = 0;
            span.addEventListener('keydown', (ev) => {
                if ((ev as KeyboardEvent).key === ' ' || (ev as KeyboardEvent).key === 'Enter') {
                    ev.preventDefault();
                    span.click();
                }
            });
            span.addEventListener('click', () => {
                const metric = span.getAttribute('data-metric') as NetIncomeMetric;
                if (!metric) return;

                if (this.selectedMetrics.has(metric)) {
                    if (this.selectedMetrics.size <= 1) return;
                    this.selectedMetrics.delete(metric);
                    span.classList.remove('active');
                } else {
                    this.selectedMetrics.add(metric);
                    span.classList.add('active');
                }

                this.config.metric = this.selectedMetrics.size === 1
                    ? Array.from(this.selectedMetrics)[0]
                    : undefined;
                this.refreshLegends();
                this.updateDisplay();
            });
        });
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
        this.bindLegendToggles();

        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        if (filtersBox) {
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async fetchMetric(symbol: string, metric: NetIncomeMetric): Promise<NetIncomeDataPoint[]> {
        const url = `${APIURL}/v1/symbol-specific/net-income-per-employee`;
        const response = await apiGet({
            method: 'GET',
            url,
            query: {
                symbol,
                startDate: this.startDate,
                endDate: this.endDate,
                metric
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) throw new Error(`Failed to fetch ${metric} data`);
        const json = await response.json();
        if (!json?.isSuccess) throw new Error(json?.message || `Failed to fetch ${metric} data`);
        return Array.isArray(json?.data?.data) ? json.data.data : [];
    }

    private mergeMetricData(
        employeeRows: NetIncomeDataPoint[],
        netIncomeRows: NetIncomeDataPoint[]
    ): NetIncomeDataPoint[] {
        const byYear = new Map<number, NetIncomeDataPoint>();

        for (const row of employeeRows) {
            const year = row.year;
            const existing = byYear.get(year) || { date: row.date, year };
            existing.employee_count = row.employee_count;
            byYear.set(year, existing);
        }

        for (const row of netIncomeRows) {
            const year = row.year;
            const existing = byYear.get(year) || { date: row.date, year };
            existing.net_income = row.net_income;
            existing.net_income_millions = row.net_income_millions;
            byYear.set(year, existing);
        }

        return Array.from(byYear.values()).sort((a, b) => a.year - b.year);
    }

    private async fetchData(symbol: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');

        try {
            const [employeeRows, netIncomeRows] = await Promise.all([
                this.fetchMetric(symbol, 'employee_count'),
                this.fetchMetric(symbol, 'net_income')
            ]);

            this.currentData = this.mergeMetricData(employeeRows, netIncomeRows);
            this.updateDisplay();
            this.emit('dataUpdate', {
                symbol,
                startDate: this.startDate,
                endDate: this.endDate,
                data: this.currentData
            });
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.currentData = [];
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
        if (this.currentData?.length) {
            chartDv?.setAttribute('data-testing-id', 'net-income-per-employee-chartdv');
        } else {
            chartDv?.removeAttribute('data-testing-id');
        }

        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private formatDateLabel(dateString: string, year: number): string {
        if (!dateString) return String(year);
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return String(year);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }

    private formatEmployeeCount(value: number | null | undefined): string {
        if (value === null || value === undefined) return '-';
        return value.toLocaleString('en-US');
    }

    private formatNetIncome(value: number | null | undefined): string {
        if (value === null || value === undefined) return '-';
        if (Math.abs(value) >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
        if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
        return `$${value.toLocaleString('en-US')}`;
    }

    private formatNetIncomePerEmployee(netIncome?: number, employeeCount?: number): string {
        if (!netIncome || !employeeCount) return '-';
        const perEmployee = netIncome / employeeCount;
        return `$${perEmployee.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#netIncomePerEmployeeChart') as HTMLElement;
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

        if (!container) return;

        const hasData = Array.isArray(this.currentData) && this.currentData.length > 0;
        if (!hasData) {
            chartDv?.removeAttribute('data-testing-id');
            container.innerHTML = getNoDataHTML();
            if (this.currentChart) {
                try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
                this.currentChart = null;
            }
            return;
        }

        const arr = this.currentData!;
        const labels = arr.map(item => this.formatDateLabel(item.date, item.year));
        const showEmployeeCount = this.selectedMetrics.has('employee_count');
        const showNetIncome = this.selectedMetrics.has('net_income');
        const series: any[] = [];
        const yAxis: any[] = [];

        if (showEmployeeCount) {
            yAxis.push({
                type: 'value',
                name: 'Employees',
                position: 'left',
                scale: true,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: showNetIncome ? false : true }
            });
            series.push({
                name: 'Employee Count',
                type: 'line',
                yAxisIndex: 0,
                smooth: true,
                showSymbol: true,
                symbolSize: 6,
                data: arr.map(item => item.employee_count ?? null),
                lineStyle: { width: 2, color: METRIC_OPTIONS[0].color },
                itemStyle: { color: METRIC_OPTIONS[0].color }
            });
        }

        if (showNetIncome) {
            yAxis.push({
                type: 'value',
                name: 'Net Income ($M)',
                position: showEmployeeCount ? 'right' : 'left',
                scale: true,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: true }
            });
            series.push({
                name: 'Net Income',
                type: 'line',
                yAxisIndex: showEmployeeCount ? 1 : 0,
                smooth: true,
                showSymbol: true,
                symbolSize: 6,
                data: arr.map(item => item.net_income_millions ?? null),
                lineStyle: { width: 2, color: METRIC_OPTIONS[1].color },
                itemStyle: { color: METRIC_OPTIONS[1].color }
            });
        }

        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }

        const chart = echarts.init(container);
        this.currentChart = chart;

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');

        yAxis.forEach(axis => {
            axis.axisLabel = { color: textColor, fontSize: 12 };
            axis.nameTextStyle = { color: textColor, fontSize: 11 };
            axis.splitLine = {
                show: false
            };
        });

        const option: any = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    if (!params || params.length === 0) return '';
                    const idx = params[0].dataIndex;
                    const row = arr[idx];
                    let html = `<div style="font-weight:600;margin-bottom:4px;">${params[0].axisValue}</div>`;
                    params.forEach((p: any) => {
                        const val = p.data;
                        const display = p.seriesName === 'Employee Count'
                            ? this.formatEmployeeCount(val)
                            : (val == null ? '-' : `$${Number(val).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`);
                        html += `<div style="display:flex;align-items:center;margin:4px 0"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:8px"></span><span>${p.seriesName}: ${display}</span></div>`;
                    });
                    if (row?.employee_count && row?.net_income) {
                        html += `<div style="margin-top:4px;">Net Income / Employee: ${this.formatNetIncomePerEmployee(row.net_income, row.employee_count)}</div>`;
                    }
                    return html;
                }
            },
            legend: { show: false },
            grid: {
                top: '30',
                left: '35',
                right: showEmployeeCount && showNetIncome ? '12' : '0',
                bottom: '0',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: labels,
                boundaryGap: false,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { color: textColor, fontSize: 12 },
                splitLine: {
                    show: false
                }
            },
            yAxis,
            series
        };

        chart.setOption(option);

        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        legendSpans.forEach(span => {
            const metric = span.getAttribute('data-metric') as NetIncomeMetric;
            span.classList.toggle('active', this.selectedMetrics.has(metric));
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol: string) => this.fetchData(symbol),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as
            | 'white'
            | 'black'
            | 'default-dark'
            | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.datePickerInstance?.destroy) {
            this.datePickerInstance.destroy();
            this.datePickerInstance = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseNetIncomePerEmployee: typeof TraderverseNetIncomePerEmployee; } }
if (typeof window !== 'undefined') { window.TraderverseNetIncomePerEmployee = TraderverseNetIncomePerEmployee; }
